import { client } from './pocketbase.js'
import { getTenantId } from './tenant.js'
import tenant from '@/store/tenant.js'

export async function listTenantCategories(tenantId) {
  const tid = tenantId || getTenantId()
  if (!tid) return []
  const result = await client.collection('categoryInventory').getFullList({
    filter: `tenant = "${tid}"`,
    sort: 'position,created',
  })
  tenant.commit('categories', result)
  return result
}

export async function listVisibleTenantCategories(tenantId) {
  const tid = tenantId || getTenantId()
  if (!tid) return []
  const result = await client.collection('categoryInventory').getFullList({
    filter: `tenant = "${tid}" && (show_on_landing = true || show_on_landing = null)`,
    sort: 'position,created',
  })
  tenant.commit('categories', result)
  return result
}

export async function createTenantCategory(tenantId, data) {
  const tid = tenantId || getTenantId()
  if (!tid) throw new Error('Missing tenant id')
  const payload = {
    tenant: tid,
    name: data.name || '',
    show_on_landing: data.show_on_landing !== undefined ? data.show_on_landing : true,
  }
  if (data.featured_item_id !== undefined) payload.featured_item_id = data.featured_item_id || ''
  if (data.featured_item_type !== undefined) payload.featured_item_type = data.featured_item_type || ''
  return client.collection('categoryInventory').create(payload)
}

export async function updateTenantCategory(id, data) {
  if (!id) throw new Error('Missing category id')
  const payload = {}
  if (data.name !== undefined) payload.name = data.name
  if (data.show_on_landing !== undefined) payload.show_on_landing = data.show_on_landing
  if (data.position !== undefined) payload.position = data.position
  if (data.featured_item_id !== undefined) payload.featured_item_id = data.featured_item_id || ''
  if (data.featured_item_type !== undefined) payload.featured_item_type = data.featured_item_type || ''
  return client.collection('categoryInventory').update(id, payload)
}

export async function deleteTenantCategory(id) {
  if (!id) throw new Error('Missing category id')
  return client.collection('categoryInventory').delete(id)
}

export function getEnabledTenantCategories(categories) {
  return (categories || []).filter((c) => (c.show_on_landing ?? true) && (c.name || '').trim())
}

export function itemMatchesEnabledCategory(item, categories) {
  const names = getEnabledTenantCategories(categories).map((c) => (c.name || '').trim())
  if (!names.length) return false

  if (item.category === 'inventory') {
    const catalog = (item.catalog || '').trim().toLowerCase()
    return names.some((name) => name.toLowerCase() === catalog)
  }

  return names.some((name) => name.toLowerCase() === 'flower')
}

export function buildSaleCategoryLookup(strains = [], inventory = []) {
  const inventoryById = new Map()
  const inventoryByName = new Map()
  const strainsById = new Map()
  const strainsByName = new Map()

  inventory.forEach((inv) => {
    const id = (inv?.id || '').toString().trim()
    const name = (inv?.name || '').toString().trim().toLowerCase()
    const catalog = (inv?.catalog || '').trim()
    const meta = { catalog, isInventory: true }
    if (id) inventoryById.set(id, meta)
    if (name) inventoryByName.set(name, meta)
  })

  strains.forEach((strain) => {
    const id = (strain?.id || '').toString().trim()
    const name = (strain?.strain || '').toString().trim().toLowerCase()
    const meta = { catalog: 'Flower', isInventory: false }
    if (id) strainsById.set(id, meta)
    if (name) strainsByName.set(name, meta)
  })

  return { inventoryById, inventoryByName, strainsById, strainsByName }
}

export function getSaleLineCategory(line, lookup) {
  if (!line || !lookup) return ''

  const id = (line?.id || '').toString().trim()
  const collection = (line?.collection || '').toString().trim().toLowerCase()
  const name = (line?.item || '').toString().trim().toLowerCase()

  let mapped = null
  if (collection === 'inventory') mapped = lookup.inventoryById.get(id)
  if (!mapped && collection === 'strains') mapped = lookup.strainsById.get(id)
  if (!mapped && id) mapped = lookup.inventoryById.get(id) || lookup.strainsById.get(id) || null
  if (!mapped && name) mapped = lookup.inventoryByName.get(name) || lookup.strainsByName.get(name) || null

  if (!mapped) return ''
  if (mapped.catalog === 'Flower') return line?.preroll ? 'Flower Preroll' : 'Flower'
  return mapped.catalog
}

function categoryNameMatchesEnabled(categoryName, enabledNames) {
  const cat = (categoryName || '').trim().toLowerCase()
  if (!cat) return false
  if (enabledNames.includes(cat)) return true
  if (cat === 'flower preroll' && enabledNames.includes('flower')) return true
  return false
}

export function saleLineMatchesEnabledCategory(line, categories, lookup) {
  const enabledNames = getEnabledTenantCategories(categories).map((c) => (c.name || '').trim().toLowerCase())
  if (!enabledNames.length) return false
  return categoryNameMatchesEnabled(getSaleLineCategory(line, lookup), enabledNames)
}

export function filterSaleForSos(sale, categories, lookup) {
  const items = Array.isArray(sale?.items) ? sale.items : []
  const visibleItems = items.filter((line) => saleLineMatchesEnabledCategory(line, categories, lookup))
  if (!visibleItems.length) return null

  const visibleTotal = visibleItems.reduce((sum, line) => {
    const amount = Number(line?.amount) || 1
    const actual = Number(line?.actualPrice)
    if (actual > 0) return sum + actual
    return sum + (Number(line?.price) || 0) * amount
  }, 0)

  return { ...sale, items: visibleItems, total: visibleTotal }
}
